import { IProductKeywordsUploadResp } from 'pages/Keywords/types';
import {
    IProductKeyword,
    IProductKeywordsExcelData,
    IVisibleProductKeywordByProduct,
    TProductKeywordQueryType,
} from 'types';
import { cdmApi } from '.';

const PREFIX = '/product-keywords';

interface IGetParams {
    page?: number;
    limit?: number;
    search?: string;
    searchBy?: TProductKeywordQueryType;
    sort?: string;
    sortBy?: string;
}

const productKeywordsApi = cdmApi.injectEndpoints({
    endpoints: (builder) => ({
        getProductKeywords: builder.query<{ code: number; product_keywords: IProductKeyword[] }, void>({
            query: (params) => ({ url: `${PREFIX}/`, method: 'get', params }),
        }),

        getProductKeywordsByProduct: builder.query<
            {
                code: number;
                product_keywords: {
                    ean: string;
                    upc?: string;
                    keywords: IProductKeyword[];
                }[];
                total_product_keywords: number;
                page: number;
                limit: number;
                search: string;
                searchBy: TProductKeywordQueryType;
                sort: string;
                sortBy: string;
            },
            { page?: number; limit?: number; search?: string; searchBy?: string; sort?: string; sortBy?: string } | void
        >({
            query: (params) => ({
                url: `${PREFIX}${params?.page ? `?page=${params?.page}` : ''}${
                    params?.limit ? `&limit=${params?.limit}` : ''
                }${params?.search ? `&search=${params?.search}` : ''}${
                    params?.searchBy ? `&searchBy=${params?.searchBy}` : ''
                }${params?.sort ? `&sort=${params?.sort}` : ''}${params?.sortBy ? `&sortBy=${params?.sortBy}` : ''}`,
                method: 'get',
            }),
            providesTags: () => [{ type: 'ProductKeywords', id: 'LIST' }],
        }),

        addProductKeywords: builder.mutation<
            { code: number; keywords_added: number; ean_found: boolean; product_keywords: IProductKeyword[] },
            { productKeywords: IVisibleProductKeywordByProduct; getParams?: IGetParams }
        >({
            query: (data) => ({
                url: `${PREFIX}/`,
                method: 'POST',
                data: data.productKeywords,
            }),
            async onQueryStarted({ productKeywords, getParams }, { dispatch, queryFulfilled }) {
                const resp = await queryFulfilled;
                // response recieved from the server
                const respProductKeywords = resp.data.product_keywords;
                const patchResult = dispatch(
                    productKeywordsApi.util.updateQueryData('getProductKeywordsByProduct', getParams, (draft) => {
                        const draftRef = draft;
                        const ean = respProductKeywords.length ? respProductKeywords[0].ean : productKeywords.ean;
                        const newProductKeywords = draftRef.product_keywords.filter((k) => k.ean !== ean);
                        newProductKeywords.push({
                            ean,
                            upc: respProductKeywords.length ? respProductKeywords[0].upc : undefined,
                            keywords: respProductKeywords,
                        });
                        draftRef.product_keywords = newProductKeywords;
                    }),
                );
                queryFulfilled.catch(() => {
                    patchResult.undo();
                });
            },
        }),

        updateProductKeywords: builder.mutation<
            { code: number; keywords_added: number; ean_found: boolean; product_keywords: IProductKeyword[] },
            { productKeywords: IVisibleProductKeywordByProduct; getParams?: IGetParams }
        >({
            query: (data) => ({
                url: `${PREFIX}/`,
                method: 'POST',
                data: data.productKeywords,
            }),
            async onQueryStarted({ productKeywords, getParams }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    productKeywordsApi.util.updateQueryData('getProductKeywordsByProduct', getParams, (draft) => {
                        const draftRef = draft;
                        const ean = productKeywords.ean;
                        const upc = productKeywords.upc;
                        const newProductKeywords =
                            productKeywords.keywords === undefined || productKeywords.keywords?.length === 0
                                ? draftRef.product_keywords.filter((k) => k.ean !== productKeywords.ean)
                                : draftRef.product_keywords.map((k) =>
                                      k.ean === ean
                                          ? { ean, upc, keywords: productKeywords.keywords as IProductKeyword[] }
                                          : k,
                                  );
                        draftRef.product_keywords = newProductKeywords;
                    }),
                );
                queryFulfilled.catch(() => {
                    patchResult.undo();
                });
            },
        }),

        deleteProductKeywords: builder.mutation<{ code: number }, { eans: string[]; getParams?: IGetParams }>({
            query: (data) => ({
                url: `${PREFIX}/`,
                method: 'DELETE',
                data: { eans: data.eans },
            }),

            async onQueryStarted({ eans, getParams }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    productKeywordsApi.util.updateQueryData('getProductKeywordsByProduct', getParams, (draft) => {
                        const draftRef = draft;
                        const newProductKeywords = draftRef.product_keywords.filter((k) => !eans.includes(k.ean));
                        draftRef.product_keywords = newProductKeywords;
                    }),
                );
                queryFulfilled.catch(() => {
                    patchResult.undo();
                });
            },
        }),

        uploadProductKeywords: builder.mutation<IProductKeywordsUploadResp, FormData>({
            query: (data) => ({ url: `${PREFIX}/excel/upload`, method: 'POST', data }),
            invalidatesTags: [{ type: 'ProductKeywords', id: 'LIST' }],
        }),

        exportProductKeywords: builder.query<any, { isTemplate?: boolean }>({
            query: (params) => ({
                url: `${PREFIX}/excel/export`,
                method: 'get',
                params,
                responseType: 'arraybuffer',
            }),
        }),

        exportProductKeywordsErrors: builder.mutation<ArrayBuffer, IProductKeywordsExcelData[]>({
            query: (data) => ({
                url: `${PREFIX}/excel/error`,
                method: 'post',
                data,
                responseType: 'arraybuffer',
            }),
        }),
    }),

    overrideExisting: false,
});

export const {
    useGetProductKeywordsQuery,
    useAddProductKeywordsMutation,
    useUpdateProductKeywordsMutation,
    useGetProductKeywordsByProductQuery,
    useDeleteProductKeywordsMutation,
    useUploadProductKeywordsMutation,
    useLazyExportProductKeywordsQuery,
    useExportProductKeywordsErrorsMutation,
} = productKeywordsApi;
